export enum ContactType {
  Company = 0,
  Person = 1,
}

export enum DealStatus {
  Open = 0,
  Won = 1,
  Lost = 2,
}

export interface Contact {
  id: string;
  type: ContactType;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  companyId?: string;
  companyName?: string;
  ownerId?: string;
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContactRequest {
  type: ContactType;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  companyId?: string;
  ownerId?: string | null;
}

export interface DealType {
  id: string;
  name: string;
  description?: string;
  customFieldsSchema?: CustomFieldsSchema;
  createdAt: string;
}

export interface DealTypeRequest {
  name: string;
  description?: string;
  customFieldsSchema?: CustomFieldsSchema;
}

export interface Deal {
  id: string;
  description?: string;
  status: DealStatus;
  dealTypeId: string;
  dealTypeName: string;
  contactId?: string;
  contactName?: string;
  customFields?: Record<string, any>;
  projectId?: string;
  projectName?: string;
  ownerId?: string;
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NewFieldDefinition {
  name: string;
  type: string; // CustomFieldType value
  options?: string[] | null;
  value?: any;
  required?: boolean | null;
}

export interface DealRequest {
  description?: string;
  status: DealStatus;
  dealTypeId?: string | null;
  contactId?: string;
  customFields?: Record<string, any>;
  projectId?: string | null;
  ownerId?: string | null;
  newDealTypeName?: string | null;
  newFields?: NewFieldDefinition[] | null;
}

// Custom field schema types for form builder UI
export enum CustomFieldType {
  Text = "text",
  Number = "number",
  Date = "date",
  Boolean = "boolean",
  Select = "select",
  Multiselect = "multiselect",
  Member = "member",
  Members = "members",
  Textarea = "textarea",
  Checklist = "checklist",
}

export interface ChecklistItem {
  text: string;
  checked: boolean;
}

export interface CustomFieldDefinition {
  name: string;
  type: CustomFieldType;
  required?: boolean;
  options?: string[]; // For select and multiselect types
}

export interface CustomFieldsSchema {
  fields: CustomFieldDefinition[];
}

export interface NoteAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  url: string;
  createdAt: string;
}

export interface Note {
  id: string;
  entityType: "contact" | "deal" | "task" | "project";
  entityId: string;
  content: string;
  attachments?: NoteAttachment[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateNoteRequest {
  entityType: "contact" | "deal" | "task" | "project";
  entityId: string;
  content: string;
}

export interface UpdateNoteRequest {
  content: string;
}

export interface ActivityNote {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  source: "contact" | "deal" | "task" | "project";
  contactId: string | null;
  contactName: string | null;
  dealId: string | null;
  dealTypeName: string | null;
  taskId: string | null;
  taskTitle: string | null;
  projectId: string | null;
  projectName: string | null;
  attachments?: NoteAttachment[];
}

// Task enums
export enum CrmTaskStatus {
  Open = 0,
  InProgress = 1,
  Completed = 2,
}

export enum CrmTaskPriority {
  Low = 0,
  Medium = 1,
  High = 2,
}

// Task interfaces
export interface CrmTask {
  id: string;
  title: string;
  description?: string;
  status: CrmTaskStatus;
  priority: CrmTaskPriority;
  dueDate?: string;
  contactId?: string;
  contactName?: string;
  dealId?: string;
  dealTypeName?: string;
  projectId?: string;
  projectName?: string;
  ownerId?: string;
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CrmTaskRequest {
  title: string;
  description?: string;
  status: CrmTaskStatus;
  priority: CrmTaskPriority;
  dueDate?: string | null;
  contactId?: string | null;
  dealId?: string | null;
  projectId?: string | null;
  ownerId?: string | null;
}

// Project interfaces
export interface Project {
  id: string;
  name: string;
  description?: string;
  contactId?: string;
  contactName?: string;
  dealsCount: number;
  tasksCount: number;
  ownerId?: string;
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectRequest {
  name: string;
  description?: string;
  contactId?: string | null;
  ownerId?: string | null;
}

export interface ReportView {
  id: string;
  name?: string | null;
  filterJson: string;
  kind: "Ephemeral" | "Saved" | string;
  createdAt: string;
  expiresAt?: string | null;
  createdBy?: string | null;
}

export interface TimelineEntry {
  id: string;
  type: "change" | "note";
  action: "created" | "updated" | "deleted" | "note";
  entityType: "contact" | "deal" | "project" | "task" | "dealtype";
  entityId: string;
  entityName?: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  noteContent?: string;
  userId?: string;
  userName?: string;
  timestamp: string;
  attachments?: NoteAttachment[];
  source?: string;
  toolName?: string | null;
}


export interface SearchResultItem {
  id: string;
  entityType: "contact" | "deal" | "task" | "project";
  title: string;
  subtitle?: string;
}

export interface SearchResponse {
  contacts: SearchResultItem[];
  deals: SearchResultItem[];
  tasks: SearchResultItem[];
  projects: SearchResultItem[];
}
